import React, { useContext, useEffect, useState, useRef } from 'react'
import { NumericFormat, PatternFormat } from 'react-number-format';
import _ from 'lodash'
import * as dayjs from 'dayjs'
import {
  Button,
  Card,
  Checkbox,
  Col,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Row,
  Select,
  Table,
  Tooltip,
  Typography
} from 'antd'
import { QuestionCircleOutlined } from '@ant-design/icons'
import UserSelect from '../../../components/UserSelect'

import { getManufacturedParts, getManufacturedPartsDataset } from '../../../calculators/manufactured-parts'

import advantageBuildingOptions from '../../../data/advantage/options'
import advantageBuildingAmounts from '../../../data/advantage/amounts'
import advantageBuildingAddons from '../../../data/advantage/addons'

import integrityBuildingOptions from '../../../data/integrity/options'
import integrityBuildingAmounts from '../../../data/integrity/amounts'
import integrityBuildingAddons from '../../../data/integrity/addons'

import integrityCattleBuildingOptions from '../../../data/integrity/cattle/options'
import integrityCattleBuildingAmounts from '../../../data/integrity/cattle/amounts'
import integrityCattleBuildingAddons from '../../../data/integrity/cattle/addons'

import { generateTableColumns, cloneObjectExcludingKeys } from '../../../helpers'
import { fetchPricingConfig, getFormulaConstants } from '../../../utils/pricingConfig'

import { userContext } from '../../../context/User.js'

const { Title } = Typography

const isProduction = process.env.REACT_APP_ENV === 'production'

export default function EditBuildingEstimate (props) {
  const [isPartsTableExpanded, setIsPartsTableExpanded] = useState(false);
  const nonCalcFields = ['status', 'dealerId', 'buildingType', 'title', 'customerName', 'customerAddress', 'phoneNumber', 'companyName', 'buildingCity', 'buildingState', 'buildingZip', 'requestedPickUpDate', 'notes']
  const user = useContext(userContext)
  const isAdmin = user.permissions.includes('manage-estimates')
  const [form] = Form.useForm()
  const [buildingType, setBuildingType] = useState('advantage')
  const [buildingOptions, setBuildingOptions] = useState(advantageBuildingOptions)
  const [buildingAmounts, setBuildingAmounts] = useState(advantageBuildingAmounts)
  const [buildingAddons, setBuildingAddons] = useState(advantageBuildingAddons)
  const [buildingDesign, setBuildingDesign] = useState(null)
  const [manufacturedParts, setManufacturedParts] = useState(getManufacturedPartsDataset(buildingType))
  const [parts, setParts] = useState([])
  const [totals, setTotals] = useState({
    sqft: 0,
    suggestedPrice: 0,
    pricePerSqFt: 0,
    weight: 0,
    hours: 0,
    cost: 0
  })

  const [pricingConfig, setPricingConfig] = useState(null)
  const [isFixed, setIsFixed] = useState(true);
  const totalsRef = useRef(null);

  useEffect(() => {
    fetchPricingConfig(buildingType).then(config => setPricingConfig(config))
  }, [buildingType])

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsFixed(!entry.isIntersecting);
      },
      {
        threshold: 0.1
      }
    );

    // Observe the "end-marker" div
    const endMarker = document.getElementById('totals-end-marker');
    if (endMarker) {
      observer.observe(endMarker);
    }

    return () => observer.disconnect();
  }, []);

  const findBuildingDesignField = () => {
    return buildingOptions.find(opt => opt.name === "buildingDesign")
  }

  const findEngineeringDrawingsAddon = () => {
    return buildingAddons.find(addon => 
      addon.slug === 'engineer-sealed-drawings-needed-for-the-building-only' || 
      addon.name.toLowerCase().includes('engineer')
    )
  }

  const calculateEstimate = async () => {
    calculateMainCoversQty()

    const values = form.getFieldsValue(true)

    const freshConfig = await fetchPricingConfig(buildingType)
    if (freshConfig) setPricingConfig(freshConfig)
    const activeConfig = freshConfig ?? pricingConfig

    const calculatedParts = getManufacturedParts(values, buildingType, activeConfig)
    const engineerDrawingsNeeded = values['engineer-sealed-drawings-needed-for-the-building-only']
    let calculatedTotals = {}

    let totalPartsCost = calculatedParts.reduce((a, b) => a + (b.cost || 0), 0) ?? 0
    
    // Subtract out Black Jack part cost since we're adding it separately to the final price
    if (values['salt-fertilizer-building']) {
      const blackJackPart = calculatedParts.find(p => p.partNumber === '31555002')
      if (blackJackPart && blackJackPart.cost) {
        totalPartsCost -= blackJackPart.cost
      }
    }
    
    calculatedTotals.cost = totalPartsCost.toFixed(2)
    calculatedTotals.hours = ((calculatedParts.reduce((a, b) => a + (b.hours || 0), 0) * 0.95) ?? 0).toFixed(2)
    calculatedTotals.sqft = Math.ceil((parseInt(values.buildingLength) * parseFloat(values.buildingWidthNumber)) ?? 0)

    const { overhead, margin, markup, fee105 } = getFormulaConstants(activeConfig)

    let basePrice = Math.ceil(
      Math.ceil(
      (
        (
          (calculatedTotals.cost / overhead) +
          (engineerDrawingsNeeded ? 1 : 0) *
          (1250 +
            (calculatedTotals.sqft > 4500 ? 750 : 0) +
            (calculatedTotals.sqft > 8000 ? 500 : 0) +
            (calculatedTotals.sqft > 12000 ? 500 : 0) +
            (calculatedTotals.sqft > 15000 ? 500 : 0) +
            (calculatedTotals.sqft > 20000 ? 500 : 0) +
            (calculatedTotals.sqft > 25000 ? 1500 : 0)
          )
        ) + fee105 * (
          (buildingType === 'advantage' ? 1 : 0) +
          (buildingType === 'integrity' ? Math.ceil(calculatedTotals.sqft / 3000) + values.solidEndwallKit : 0) +
          (buildingType === 'integrity-cattle' ? Math.ceil(calculatedTotals.sqft / 3000) + values.doorEndwallKit : 0)
        )
      ) / margin) / 10) * 10

    calculatedTotals.suggestedPrice = ((basePrice + (values['salt-fertilizer-building'] ? 200 : 0)) * markup).toFixed(2)

    calculatedTotals.discountPercentage = parseFloat(values.discountPercentage)
    calculatedTotals.amountDue = (calculatedTotals.suggestedPrice - ((parseFloat(calculatedTotals.discountPercentage) / 100) * calculatedTotals.suggestedPrice)).toFixed(2)
    calculatedTotals.deposit = ((50 / 100) * calculatedTotals.amountDue).toFixed(2)
    calculatedTotals.amountDueAtPickup = (calculatedTotals.amountDue - calculatedTotals.deposit).toFixed(2)
    calculatedTotals.pricePerSqFt = ((calculatedTotals.suggestedPrice / calculatedTotals.sqft) ?? 0).toFixed(2)
    calculatedTotals.weight = (calculatedParts.reduce((a, b) => a + (b.weight || 0), 0) ?? 0).toFixed(2)

    const results = { totals: calculatedTotals, parts: calculatedParts }

    setParts(results.parts)
    setTotals(results.totals)

    return results
  }

  const calculateMainCoversQty = () => {
    let qty = 0
    const values = form.getFieldsValue(true)
    const buildingLength = parseInt(values.buildingLength)
    const rafterSpacing = parseInt(values.rafterSpacing)
    
    switch (values.mainCoverTerminationStyle) {
      case 'individual-bay-keder':
        qty = (buildingLength / rafterSpacing)
        break
      case 'hybrid':
      case 'sectional-keder':
        switch (rafterSpacing) {
          case 18:
            qty = buildingLength <= 54 ? 1 : (buildingLength / 54)
            break
          case 6:
          case 12:
          case 20:
            qty = buildingLength <= 60 ? 1 : (buildingLength / 60)
            break
          case 4:
          case 8:
          case 16:
            qty = buildingLength <= 64 ? 1 : (buildingLength / 64)
            break
          case 5:
          case 10:
          case 14:
            qty = buildingLength <= 70 ? 1 : (buildingLength / 70)
            break
        }
        break
      case 'monolithic':
        switch (rafterSpacing) {
          case 8:
          case 12:
          case 16:
            qty = buildingLength <= 96 ? 1 : (buildingLength / 96)
            break
          case 14:
            qty = buildingLength <= 98 ? 1 : (buildingLength / 98)
            break
          case 4:
          case 5:
          case 10:
            qty = buildingLength <= 100 ? 1 : (buildingLength / 100)
            break
          case 6:
            qty = buildingLength <= 102 ? 1 : (buildingLength / 102)
            break
        }
        break
    }

    qty = Math.ceil(qty)

    if (form && form.setFieldValue) {
      form.setFieldValue('numberOfMainCovers', qty)
    }
  }

  const getInitialValues = () => {
    const initialValues = Object.assign(
      {
        title: props.data?.title,
        buildingLength: 0,
        buildingType: props.data?.buildingType ?? 'advantage',
        dealerId: props.data?.dealerId ?? user.id,
        customerName: props.data?.customerName ?? '',
        customerAddress: props.data?.customerAddress ?? '',
        phoneNumber: props.data?.phoneNumber ?? '',
        companyName: props.data?.companyName ?? '',
        buildingCity: props.data?.buildingCity ?? '',
        buildingState: props.data?.buildingState ?? '',
        buildingZip: props.data?.buildingZip ?? '',
        requestedPickUpDate: props.data?.requestedPickUpDate ? dayjs(props.data.requestedPickUpDate) : '',
        status: props.data?.status ?? 'draft',
        discountPercentage: props.data?.discountPercentage ?? 30.00,
        notes: props.data?.notes ?? ''
      },
      buildingOptions.reduce((acc, cur) => ({ ...acc, [cur.name]: cur.value }), {}),
      buildingAmounts.reduce((acc, cur) => ({ ...acc, [cur.name]: cur.value }), {}),
      buildingAddons.reduce((acc, cur) => ({ ...acc, [cur.slug]: cur.checked }), {}),
      props.data?.inputData ?? {}
    )

    return initialValues
  }

  const handlePartAdd = () => {
    const newData = parts.concat([{
      requiredQty: 0,
      addQty: 0
    }])

    setParts(newData)
  }

  const handleCancel = () => {
    window.location.href = '/';
    if (props.onCancel) {
      props.onCancel()
    }
  }

  const handlePartChange = (value, item, index, record) => {
    let newData = [...parts]
    let newPart = manufacturedParts.find(p => p.partNumber === value)

    index = newData.findIndex(p => p.partNumber === record.partNumber)

    newData.splice(index, 1)
    newData.push(Object.assign({ requiredQty: 0, addQty: record.addQty }, newPart))

    setParts(newData)
  }

  const onBuildingTypeChange = (type) => {
    setBuildingType(type)
    setManufacturedParts(getManufacturedPartsDataset(type))
    
    switch(type) {
      case 'integrity':
        setBuildingOptions(integrityBuildingOptions)
        setBuildingAmounts(integrityBuildingAmounts)
        setBuildingAddons(integrityBuildingAddons)
        break
      case 'integrity-cattle':
        setBuildingOptions(integrityCattleBuildingOptions)
        setBuildingAmounts(integrityCattleBuildingAmounts)
        setBuildingAddons(integrityCattleBuildingAddons)
        break
      case 'advantage':
      default:
        setBuildingOptions(advantageBuildingOptions)
        setBuildingAmounts(advantageBuildingAmounts)
        setBuildingAddons(advantageBuildingAddons)
    }
  }

  const onChange = (updated, values) => {
    const updatedKeys = Object.keys(updated || {})
    const updatedOptionsWithHiddenFields = buildingOptions.filter(item => item.hiddenField && updatedKeys.includes(item.name))
    const designField = findBuildingDesignField()

    if (designField && updatedKeys.includes(designField.name)) {
      setBuildingDesign(updated[designField.name])
      
      if (updated[designField.name] === 'lc2') {
        const engineeringAddon = findEngineeringDrawingsAddon();
        if (engineeringAddon && form) {
          form.setFieldValue(engineeringAddon.slug, false);
        }
      }
    }

    // Handle Ground Mount / Ground Mount Cover synchronization
    if (updatedKeys.includes('fabricTermination')) {
      if (updated.fabricTermination === 'ground-mount') {
        form.setFieldValue('ground-mount-cover', true);
      }
    }
    
    if (updatedKeys.includes('ground-mount-cover')) {
      if (updated['ground-mount-cover'] === false) {
        const currentFabricTermination = form.getFieldValue('fabricTermination');
        if (currentFabricTermination === 'ground-mount') {
          form.setFieldValue('fabricTermination', 'standard');
        }
      } else if (updated['ground-mount-cover'] === true) {
        const currentFabricTermination = form.getFieldValue('fabricTermination');
        if (currentFabricTermination !== 'ground-mount') {
          form.setFieldValue('fabricTermination', 'ground-mount');
        }
      }
    }

    if (updatedOptionsWithHiddenFields.length) {
      updatedOptionsWithHiddenFields.forEach(option => {
        const selectedOption = option.options.find(item => item.value === updated[option.name])

        if (form && form.setFieldValue) {
          form.setFieldValue(option.hiddenField, selectedOption?.hiddenfieldvalue)
        }
      })
    }

    if (!Object.keys(values).length || !updatedKeys.some(key => nonCalcFields.includes(key))) {
      calculateEstimate()
    }
  }

  const onFinish = (values) => {
    return props.onFinish({ ...values, parts, totals, type: 'building' })
  }

  useEffect(() => {
    if (props.data) {
      if (props.data.inputData?.buildingType) {
        onBuildingTypeChange(props.data.inputData.buildingType)
      }

      if (props.data.parts) {
        setParts(props.data.parts)
      }
  
      if (props.data.totals) {
        setTotals(props.data.totals)
      }
    }
  }, [])

  useEffect(() => {
    if (form && form.setFieldsValue) {
      const initialValues = getInitialValues()
      const formValues = cloneObjectExcludingKeys(initialValues, nonCalcFields)
      form.setFieldsValue(formValues)
      onChange(initialValues, {})
    }
  }, [ buildingType ])

  const designField = findBuildingDesignField();
  const engineeringAddon = findEngineeringDrawingsAddon();

  const filteredBuildingAddons = engineeringAddon 
    ? buildingAddons.filter(addon => addon.slug !== engineeringAddon.slug)
    : buildingAddons;

  return (
    <>
      <Title level={2}>{ (props.data ? 'Edit' : 'New') + ' Building Estimate' }</Title>

      <Form
        form={ form }
        initialValues={ getInitialValues() }
        layout="vertical"
        onValuesChange={ onChange }
        onFinish={ onFinish }
        scrollToFirstError
      >
        { isAdmin &&
          <Row gutter={24} className="compact-row">
            <Col span={3}>
              <Form.Item name="status" label="Estimate Status" style={{ marginBottom: '8px' }}>
                <Select options={[
                  { label: 'Draft', value: 'draft' },
                  { label: 'Pending', value: 'pending' },
                  { label: 'Approved', value: 'approved' },
                  { label: 'Ordered', value: 'ordered' },
                  { label: 'Complete', value: 'complete' },
                ]} />
              </Form.Item>
            </Col>

            <Col span={6}>
              <Form.Item name="dealerId" label="Assigned Dealer" style={{ marginBottom: '8px' }}>
                <UserSelect />
              </Form.Item>
            </Col>
          </Row>
        }

        <Row gutter={24} className="compact-row">
          <Col span={6}>
            <Form.Item name="buildingType" label="Building Type" style={{ marginBottom: '8px' }}>
              <Select onChange={onBuildingTypeChange} options={
                [
                  { label: 'Advantage', value: 'advantage' },
                  { label: 'Integrity', value: 'integrity' },
                  { label: 'Integrity Cattle', value: 'integrity-cattle' }
                ]
              } />
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item name="title" label="Project Name" style={{ marginBottom: '8px' }}>
              <Input />
            </Form.Item>
          </Col>
        </Row>

        {buildingType === 'integrity-cattle' && (
          <div style={{
            backgroundColor: 'white',
            border: '1px solid #8B1F41',
            borderRadius: '6px',
            padding: '12px 16px',
            marginBottom: '16px',
            color: '#8B1F41',
            display: 'inline-block',
            maxWidth: 'fit-content'
          }}>
            <strong>Note:</strong> Cattle Buildings set on beams are calculated on 16' centers. If the snow load requires truss spacing below 16', please reach out to Accu-Steel for an estimate.
          </div>
        )}

        <Row gutter={24} className="compact-row">
          <Col span={6}>
            <Form.Item name="customerName" label="Customer Name" style={{ marginBottom: '8px' }}>
              <Input />
            </Form.Item>
          </Col>

          <Col span={8}>
            <Form.Item name="customerAddress" label="Customer Address" style={{ marginBottom: '8px' }}>
              <Input />
            </Form.Item>
          </Col>

          <Col span={5}>
            <Form.Item name="phoneNumber" label="Phone Number" style={{ marginBottom: '8px' }}>
              <PatternFormat
                format="(###) ###-####"
                mask="_"
                customInput={Input}
                placeholder="(555) 123-4567"
              />
            </Form.Item>
          </Col>

          <Col span={5}>
            <Form.Item name="requestedPickUpDate" label="Requested Pick-Up Date" style={{ marginBottom: '8px' }}>
              <DatePicker format={'MM/DD/YYYY'}/>
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={24} className="compact-row">
          <Col span={6}>
            <Form.Item name="companyName" label="Company Name" style={{ marginBottom: '8px' }}>
              <Input />
            </Form.Item>
          </Col>

          <Col span={6}>
            <Form.Item name="buildingCity" label="Building City" style={{ marginBottom: '8px' }}>
              <Input />
            </Form.Item>
          </Col>

          <Col span={4}>
            <Form.Item name="buildingState" label="Building State" style={{ marginBottom: '8px' }}>
              <Select
                showSearch
                placeholder="Select state"
                filterOption={(input, option) =>
                  option.label.toLowerCase().includes(input.toLowerCase())
                }
                options={[
                  { label: 'AL', value: 'AL' },
                  { label: 'AK', value: 'AK' },
                  { label: 'AZ', value: 'AZ' },
                  { label: 'AR', value: 'AR' },
                  { label: 'CA', value: 'CA' },
                  { label: 'CO', value: 'CO' },
                  { label: 'CT', value: 'CT' },
                  { label: 'DE', value: 'DE' },
                  { label: 'FL', value: 'FL' },
                  { label: 'GA', value: 'GA' },
                  { label: 'HI', value: 'HI' },
                  { label: 'ID', value: 'ID' },
                  { label: 'IL', value: 'IL' },
                  { label: 'IN', value: 'IN' },
                  { label: 'IA', value: 'IA' },
                  { label: 'KS', value: 'KS' },
                  { label: 'KY', value: 'KY' },
                  { label: 'LA', value: 'LA' },
                  { label: 'ME', value: 'ME' },
                  { label: 'MD', value: 'MD' },
                  { label: 'MA', value: 'MA' },
                  { label: 'MI', value: 'MI' },
                  { label: 'MN', value: 'MN' },
                  { label: 'MS', value: 'MS' },
                  { label: 'MO', value: 'MO' },
                  { label: 'MT', value: 'MT' },
                  { label: 'NE', value: 'NE' },
                  { label: 'NV', value: 'NV' },
                  { label: 'NH', value: 'NH' },
                  { label: 'NJ', value: 'NJ' },
                  { label: 'NM', value: 'NM' },
                  { label: 'NY', value: 'NY' },
                  { label: 'NC', value: 'NC' },
                  { label: 'ND', value: 'ND' },
                  { label: 'OH', value: 'OH' },
                  { label: 'OK', value: 'OK' },
                  { label: 'OR', value: 'OR' },
                  { label: 'PA', value: 'PA' },
                  { label: 'RI', value: 'RI' },
                  { label: 'SC', value: 'SC' },
                  { label: 'SD', value: 'SD' },
                  { label: 'TN', value: 'TN' },
                  { label: 'TX', value: 'TX' },
                  { label: 'UT', value: 'UT' },
                  { label: 'VT', value: 'VT' },
                  { label: 'VA', value: 'VA' },
                  { label: 'WA', value: 'WA' },
                  { label: 'WV', value: 'WV' },
                  { label: 'WI', value: 'WI' },
                  { label: 'WY', value: 'WY' }
                ]}
              />
            </Form.Item>
          </Col>

          <Col span={4}>
            <Form.Item name="buildingZip" label="Building Zip" style={{ marginBottom: '8px' }}>
              <Input />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={24}>
          <Col span={6}>
            <Form.Item name="buildingLength" label="Building Length" rules={[ { required: true } ]}>
              <InputNumber />
            </Form.Item>
          </Col>
          {
            buildingOptions.map((opt, i) => {
              const isBuildingDesignField = designField && opt.name === designField.name;
              
              return (
                <Col key={i} span={6}>
                  <Form.Item name={opt.name} label={opt.label} tooltip={opt.info}>
                    <Select options={opt.options} />
                  </Form.Item>

                  {isBuildingDesignField && engineeringAddon && (
                    <Form.Item
                      name={engineeringAddon.slug}
                      valuePropName="checked"
                      style={{ 
                        marginTop: '-12px',
                        marginBottom: '8px'
                      }}
                    >
                      <Checkbox disabled={buildingDesign === 'lc2'}>
                        <span style={{ fontSize: '13px' }}>
                          {engineeringAddon.name}
                          {buildingDesign === 'lc2' && (
                            <span style={{ marginLeft: '6px', fontSize: '12px', color: '#ff4d4f' }}>
                              (LC1 only)
                            </span>
                          )}
                        </span>
                      </Checkbox>
                    </Form.Item>
                  )}

                  {opt.hiddenField &&
                    <Form.Item name={opt.hiddenField} hidden>
                      <Input type="hidden" />
                    </Form.Item>
                  }
                </Col>
              )
            })
          }
        </Row>

        <Row gutter={24}>
          {
            buildingAmounts.map((opt, i) => {
              return (
                <Col key={i} span={6}>
                  <Form.Item name={opt.name} label={opt.label} tooltip={opt.info}>
                    <InputNumber min={opt.min} max={opt.max} />
                  </Form.Item>
                </Col>
              )
            })
          }
        </Row>

        <Row gutter={24} className="compact-addons-row">
          {
            buildingAddons.map((opt, i) => {
              const isEngineeringAddon = engineeringAddon && opt.slug === engineeringAddon.slug;
              
              return (
                <Col key={i} span={8}>
                  <Form.Item name={opt.slug} valuePropName="checked" style={{ margin: '0px' }}>
                    <Checkbox disabled={isEngineeringAddon && buildingDesign === 'lc2' && buildingType !== 'integrity-cattle'}>
                      <span>{opt.name}</span>
                      { opt.info &&
                        <Tooltip title={opt.info}><QuestionCircleOutlined /></Tooltip>
                      }
                      {isEngineeringAddon && buildingDesign === 'lc2' && buildingType !== 'integrity-cattle' &&  (
                        <span style={{ 
                          marginLeft: '6px', 
                          fontSize: '12px', 
                          color: '#ff4d4f' 
                        }}>
                          (LC1 only)
                        </span>
                      )}
                    </Checkbox>
                  </Form.Item>
                </Col>
              )
            })
          }
        </Row>

        <div>
          <div style={{ 
            border: '1px solid #e5e7eb', 
            borderRadius: '8px',
            marginBottom: '16px',
            boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)'
          }}>
            <div 
              onClick={() => setIsPartsTableExpanded(!isPartsTableExpanded)} 
              style={{ 
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '12px 24px',
                backgroundColor: 'white',
                cursor: 'pointer',
                borderBottom: '1px solid #e5e7eb'
              }}
            >
              <span style={{ 
                fontWeight: 500,
                color: '#1f2937'
              }}>Parts / Part Numbers</span>
              <Button 
                type="primary"
                size="medium"
                style={{
                  backgroundColor: '#8B1F41',
                  borderColor: '#8B1F41',
                  pointerEvents: 'none'
                }}
              >
                {isPartsTableExpanded ? 'Close' : 'Open'}
              </Button>
            </div>
            
            {isPartsTableExpanded && (
              <div style={{ padding: '16px' }}>
                <Table
                  bordered
                  dataSource={parts.map((p, i) => Object({ ...p, key: i }))}
                  pagination={false}
                  size="small"
                  rowKey="key"
                  rowClassName={(item) => (!item.id) ? 'no-expand' : null}
                  columns={[
                    {
                      title: 'Part / Part Number',
                      dataIndex: 'name',
                      render: (item, record, index) => {                  
                        if (record.requiredQty) {
                          let displayDescription = record.description
                          let displayPartNumber = record.partNumber ?? `ID #${record.id}`
        
                          if (record.alternate && form && form.getFieldValue) {
                            const fieldValue = form.getFieldValue(record.alternate.field)
  
                            if (fieldValue === record.alternate.value) {
                              if (record.alternate.properties.description) {
                                displayDescription = record.alternate.properties.description
                              }
  
                              if (record.alternate.properties.partNumber) {
                                displayPartNumber = record.alternate.properties.partNumber
                              }
                            }
                          }
  
                          return (
                            <div className="part">
                              <span className="part-name">{ displayDescription }</span>
                              <span className="part-number">{ displayPartNumber }</span>
                            </div>
                          )
                        }
  
                        return (
                          <Form.Item>
                            <Select
                              showSearch
                              placeholder="Select a part"
                              optionFilterProp="children"
                              filterOption={ (input, option) => {
                                return (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                              }}
                              options={
                                manufacturedParts
                                  // Filter out parts that are already accounted for (but keep currently selected part)
                                  .filter(part => !parts.map(a => a.partNumber).includes(part.partNumber) || part.partNumber === record.partNumber)
  
                                  // Format option field
                                  .map(part => {
                                    let displayDescription = part.description
                                    let displayPartNumber = part.partNumber ?? `ID #${part.id}`
                  
                                    if (part.alternate && form && form.getFieldValue) {
                                      const fieldValue = form.getFieldValue(part.alternate.field)
                
                                      if (fieldValue === part.alternate.value) {
                                        if (part.alternate.properties.description) {
                                          displayDescription = part.alternate.properties.description
                                        }
                
                                        if (part.alternate.properties.partNumber) {
                                          displayPartNumber = part.alternate.properties.partNumber
                                        }
                                      }
                                    }
  
                                    return {
                                      label: `${displayDescription} (${displayPartNumber})`,
                                      value: part.partNumber ?? part.id
                                    }
                                  })
                              }
                              onChange={ (value, item) => handlePartChange(value, item, index, record) }
                              style={{ display: 'block' }}
                              value={ record.partNumber }
                            />
                          </Form.Item>
                        )
                      }
                    },
                    {
                      title: 'Required Quantity',
                      dataIndex: 'requiredQty',
                      render: (item, record, index) => (
                        <NumericFormat value={record.requiredQty} displayType={'text'} thousandSeparator={true} />
                      )
                    },
                    {
                      title: 'Additional Quantity',
                      dataIndex: 'addQty',
                      render: (item, record, index) => (
                        <Form.Item name={['additions', record.partNumber]} initialValue={ record.addQty }>
                          <InputNumber />
                        </Form.Item>
                      )
                    }
                  ]}

                  expandable={{
                    rowExpandable: (item) => isAdmin,
                    expandedRowRender: (item) => {
                      return (
                        <>
                          <Table
                            columns={ generateTableColumns(['weight', 'hours', 'cost']) }
                            dataSource={ [ item ] }
                            pagination={ false }
                            rowKey={ (item) => 'partsInfo_' + item.id }
                            rowClassName={ (item) => (!item.parts.length) ? 'no-expand' : null }
                            size='small'
                            expandable={{
                              expandedRowRender: (item) => {
                                return (
                                  <>
                                    <h3>Parts</h3><br />
                                    <Table
                                      columns={ generateTableColumns(['id', 'description', 'weight', 'galv', 'steelMrk', 'steelDrop', 'steelCost', 'fastener', 'cover', 'labor', 'totalMrk']) }
                                      dataSource={ item.parts }
                                      pagination={ false }
                                      rowKey={ (item) => 'partsInfoParts_' + item.id }
                                      rowClassName={ (item) => (!item.materials.length) ? 'no-expand' : null }
                                      size='small'
                                      expandable={{
                                        expandedRowRender: (item) => {
                                          return (
                                            <>
                                              <h4>Materials</h4><br />
                                              <Table
                                                columns={ generateTableColumns(['id', 'description', 'unitType', 'unitDivisor', 'pricePerPound', 'pricePerUnit', 'poundsPerStock', 'poundsPerUnit']) }
                                                dataSource={ item.materials }
                                                pagination={ false }
                                                rowKey={ (item) => 'partsInfoMaterials_' + item.id }
                                                size='small'
                                              />
                                            </>
                                          )
                                        }
                                      }}
                                    />
                                  </>
                                )
                              }
                            }}
                          />
                        </>
                      )
                    }
                  }}

                />

                <Button
                  onClick={handlePartAdd}
                  type="primary"
                  style={{
                    marginTop: '16px',
                    marginBottom: '16px',
                    backgroundColor: '#8B1F41',
                    borderColor: '#8B1F41'
                  }}>
                  Add a Part
                </Button>
              </div>
            )}
          </div>
        </div>

        { isAdmin &&
          <Row gutter={24}>
            <Col span={6}>
              <Form.Item name="discountPercentage" label="Retail Price Discount Percentage">
                <InputNumber min="0" max="100" step=".01" />
              </Form.Item>
            </Col>
          </Row>
        }

        <div id="totals-end-marker" style={{ height: '1px' }} />

        <Row>      
          <div 
            ref={totalsRef}
            style={{ 
              position: isFixed ? 'fixed' : 'static',
              bottom: isFixed ? 0 : 'auto',
              left: isFixed ? '200px' : 'auto',
              right: isFixed ? 0 : 'auto',
              zIndex: 100,
              backgroundColor: 'white',
              borderTop: '1px solid #e5e7eb',
              padding: '8px', 
              boxShadow: isFixed ? '0 -2px 8px rgba(0,0,0,0.1)' : 'none'
            }}
          >
            <Card 
              title="Totals" 
              style={{ 
                width: '100%',
                marginBottom: 0
              }}
              bodyStyle={{ 
                padding: '0 12px',
                margin: '0'
              }}
            >
              <Row gutter={24} style={{ margin: '16px 0 8px 0' }}>
                <Col span={8}>
                  <div >
                    <p ><b>Sqft</b>: <NumericFormat value={totals.sqft} displayType={'text'} thousandSeparator={true} decimalScale={0} /></p>
                    <p ><b>Shipping Weight</b>: <NumericFormat value={totals.weight} displayType={'text'} thousandSeparator={true} /></p>
                  </div>
                </Col>
                <Col span={8}>
                  <div>
                    <p ><b>Suggested Retail Price</b>: <NumericFormat value={totals.suggestedPrice} displayType={'text'} thousandSeparator={true} prefix={'$'} /></p>
                    { isAdmin && <p ><b>Dealer Discount</b>: <NumericFormat value={totals.discountPercentage} displayType={'text'} suffix={'%'} /></p> }
                    <p ><b>Dealer Price</b>: <NumericFormat value={totals.amountDue} displayType={'text'} thousandSeparator={true} prefix={'$'} /></p>
                  </div>
                </Col>
                <Col span={8}>
                  <div>
                    <p ><b>Price Per Sqft</b>: <NumericFormat value={totals.pricePerSqFt} displayType={'text'} thousandSeparator={true} prefix={'$'} /></p>
                    <p ><b>Deposit Required</b>: <NumericFormat value={totals.deposit} displayType={'text'} thousandSeparator={true} prefix={'$'} /></p>
                    <p ><b>Amount Due at Pickup</b>: <NumericFormat value={totals.amountDueAtPickup} displayType={'text'} thousandSeparator={true} prefix={'$'} /></p>
                  </div>
                </Col>
              </Row>
            </Card>
          </div>
        </Row>

        <Row gutter={24}>
          <Col span={24}>
            <Form.Item
              name="notes"
              label="Note"
              tooltip="Add any additional notes or comments about this estimate"
            >
              <Input.TextArea
                rows={4}
                placeholder="Enter any additional notes here..."
              />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={24}>
          <Col>
            <Form.Item>
              <Button onClick={ handleCancel }>Cancel</Button>
            </Form.Item>
          </Col>

          { !isAdmin &&
            <>
              <Col>
                <Form.Item name="status">
                  <Button htmlType="submit" onClick={ (e) => form.setFieldValue('status', 'draft') }>Save As Draft</Button>
                </Form.Item>
              </Col>

              <Col>
                <Form.Item name="status">
                  <Button type="primary" htmlType="submit" onClick={ (e) => form.setFieldValue('status', 'pending') }>Submit Order</Button>
                </Form.Item>
              </Col>
            </>
          }

          { isAdmin &&
            <>
              <Col>
                <Form.Item>
                  <Button type="primary" htmlType="submit">Save Estimate</Button>
                </Form.Item>
              </Col>
            </>
          }
        </Row>
      </Form>
    </>
  )
}
